import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, Button, Space, Card, Modal, Form, Input, Select, Tag, Popconfirm, message, Tooltip, 
  Upload, Alert, Typography, Divider, Tabs, Progress, Row, Col, Statistic, Empty, Spin 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, UploadOutlined, DownloadOutlined, FileTextOutlined, 
  CheckCircleOutlined, ExclamationCircleOutlined, BookOutlined, BarChartOutlined, VideoCameraOutlined, 
  TrophyOutlined, ClockCircleOutlined, EyeOutlined, SyncOutlined, GlobalOutlined, PlaySquareOutlined,
  BankOutlined, PhoneOutlined, MailOutlined 
} from '@ant-design/icons';
import {
  fetchAccounts, checkAccountUsername, createAccount, updateAccount, deleteAccount, bulkCreateAccounts, bulkDeleteAccounts
} from '../../../../Service/Accounts_API/accountsApi';
import { fetchInstitutions } from '../../../../Service/Institutions_API/institutionsApi';
import { fetchCourses } from '../../../../Service/Courses_API/coursesApi';
import { fetchUserWatchAnalyticsById } from '../../../../Service/VideoWatchTrack_API/videoWatchTrackApi';
import { fetchUserAssessmentAttempts } from '../../../../Service/Assessment_API/assessmentApi';

const { Text } = Typography;

const formatDate = (dateInput) => {
  if (!dateInput) return null;
  const safeStr = typeof dateInput === 'string' ? dateInput.replace(' ', 'T') : dateInput;
  let date = new Date(safeStr);
  if (isNaN(date.getTime())) {
    date = new Date(dateInput);
    if (isNaN(date.getTime())) return dateInput;
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateTime = (dateInput) => {
  if (!dateInput) return null;
  const safeStr = typeof dateInput === 'string' ? dateInput.replace(' ', 'T') : dateInput;
  let date = new Date(safeStr);
  if (isNaN(date.getTime())) {
    date = new Date(dateInput);
    if (isNaN(date.getTime())) return dateInput;
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, '0');
  return `${day}-${month}-${year} ${strHours}:${minutes} ${ampm}`;
};

const parseCSV = (csvText, currentRole = 'USER') => {
  const lines = [];
  let curLine = [];
  let curToken = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        curToken += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      curLine.push(curToken.trim());
      curToken = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') i++;
      curLine.push(curToken.trim());
      if (curLine.some(cell => cell.length > 0)) {
        lines.push(curLine);
      }
      curLine = [];
      curToken = '';
    } else {
      curToken += char;
    }
  }
  if (curToken || curLine.length > 0) {
    curLine.push(curToken.trim());
    if (curLine.some(cell => cell.length > 0)) {
      lines.push(curLine);
    }
  }

  if (lines.length === 0) return [];

  const headers = lines[0].map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, ''));
  
  const mapHeader = (headerName) => {
    if (headerName === 'institution_id' || headerName.includes('inst_id')) return 'institution_id';
    if (headerName.includes('fullname') || headerName.includes('full_name') || headerName === 'name') return 'login_name';
    if (headerName.includes('username') || headerName === 'user') return 'login_username';
    if (headerName.includes('password') || headerName === 'pass') return 'login_password';
    if (headerName.includes('contact') || headerName.includes('phone') || headerName.includes('mobile')) return 'contact';
    if (headerName.includes('email') || headerName.includes('mail')) return 'email';
    if (headerName.includes('role')) return 'login_role';
    if (headerName.includes('institution')) return 'institution_name';
    if (headerName.includes('expiry') || headerName.includes('end_date') || headerName.includes('date')) return 'login_end_date';
    if (headerName.includes('course')) return 'courses';
    if (headerName.includes('access')) return 'courses_access_type';
    return headerName;
  };

  const mappedHeaders = headers.map(mapHeader);

  const result = [];
  for (let i = 1; i < lines.length; i++) {
    const row = lines[i];
    const userObj = { rowNumber: i + 1, isValid: true, validationErrors: [] };
    
    mappedHeaders.forEach((key, idx) => {
      userObj[key] = row[idx] || '';
    });

    if (!userObj.login_username) {
      userObj.isValid = false;
      userObj.validationErrors.push('Missing Username');
    }
    if (!userObj.login_password) {
      userObj.isValid = false;
      userObj.validationErrors.push('Missing Password');
    }
    if (!userObj.login_role) {
      userObj.login_role = 'USER';
    }

    if (currentRole === 'STUDIO' && (userObj.login_role.toUpperCase() === 'ADMIN' || userObj.login_role.toUpperCase() === 'STUDIO')) {
      userObj.isValid = false;
      userObj.validationErrors.push('Studio users can only create USER accounts');
    }

    result.push(userObj);
  }

  return result;
};

const AccountsManagementPage = ({ userRole }) => {
  const currentRole = userRole || localStorage.getItem('role') || 'USER';
  const isStudio = currentRole === 'STUDIO';

  const [data, setData] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Single Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [resettingItem, setResettingItem] = useState(null);
  
  // Bulk Upload state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [parsedUsers, setParsedUsers] = useState([]);
  const [globalCourseIds, setGlobalCourseIds] = useState([]);
  const [globalAccessType, setGlobalAccessType] = useState('LIVE/VOD');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Search and Filter state
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState(undefined);
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // User Analytics Modal state
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [analyticsUser, setAnalyticsUser] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [watchAnalytics, setWatchAnalytics] = useState(null);
  const [assessmentAnalytics, setAssessmentAnalytics] = useState(null);
  const [selectedAttemptBreakdown, setSelectedAttemptBreakdown] = useState(null);
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);

  const handleOpenAnalytics = async (userRecord) => {
    setAnalyticsUser(userRecord);
    setIsAnalyticsModalOpen(true);
    setAnalyticsLoading(true);
    try {
      const [watchRes, assessRes] = await Promise.allSettled([
        fetchUserWatchAnalyticsById(userRecord.login_id),
        fetchUserAssessmentAttempts(userRecord.login_id)
      ]);

      if (watchRes.status === 'fulfilled' && watchRes.value) {
        setWatchAnalytics(watchRes.value);
      } else {
        setWatchAnalytics(null);
      }

      if (assessRes.status === 'fulfilled' && assessRes.value) {
        setAssessmentAnalytics(assessRes.value);
      } else {
        setAssessmentAnalytics(null);
      }
    } catch (err) {
      console.error('Failed to load user analytics:', err);
      message.error('Failed to load user analytics data.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleRefreshAnalytics = () => {
    if (analyticsUser) {
      handleOpenAnalytics(analyticsUser);
    }
  };

  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (selectedRole && selectedRole !== 'all') params.role = selectedRole;
      if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus;
      const result = await fetchAccounts(params);
      setData(result);
    } catch (e) {
      message.error('Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedRole, selectedStatus]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  useEffect(() => {
    loadData();
    loadInstitutionsList();
    loadCoursesList();
  }, [loadData]);

  const loadInstitutionsList = async () => {
    try {
      const list = await fetchInstitutions();
      setInstitutions(list);
    } catch (e) {
      console.warn('Failed to load institutions list.');
    }
  };

  const loadCoursesList = async () => {
    try {
      const list = await fetchCourses();
      setCourses(list);
    } catch (e) {
      console.warn('Failed to load courses list.');
    }
  };

  const handleSave = async (values) => {
    try {
      const cleanUsername = (values.login_username || '').trim();
      const payload = {
        login_name: values.login_name,
        contact: values.contact ? values.contact.trim() : null,
        email: values.email ? values.email.trim() : null,
        login_username: cleanUsername,
        login_role: values.login_role,
        login_status: values.login_status,
        login_end_date: values.login_end_date || null,
        institution_id: values.institution_id ? parseInt(values.institution_id) : null,
        course_ids: values.course_ids || [],
        courses_access_type: values.courses_access_type || 'LIVE/VOD'
      };

      if (editingItem) {
        await updateAccount(editingItem.login_id, payload);
        message.success('Account updated successfully.');
      } else {
        const localExists = data.some(
          acc => (acc.login_username || '').trim().toLowerCase() === cleanUsername.toLowerCase()
        );
        if (localExists) {
          message.error('Username already exists. Please choose a different username.');
          return;
        }

        await createAccount({
          ...payload,
          login_password: values.login_password
        });
        message.success('Account created successfully.');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingItem(null);
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to save account.');
    }
  };

  const handleResetPassword = async (values) => {
    try {
      await updateAccount(resettingItem.login_id, {
        ...resettingItem,
        login_end_date: resettingItem.login_end_date ? resettingItem.login_end_date.split('T')[0] : null,
        institution_id: resettingItem.institution_id ? parseInt(resettingItem.institution_id) : null,
        login_password: values.login_password
      });
      message.success(`Password for ${resettingItem.login_username} reset successfully.`);
      setIsResetModalOpen(false);
      resetForm.resetFields();
      setResettingItem(null);
    } catch (e) {
      message.error(e.message || 'Failed to reset password.');
    }
  };

  
  const handleDeleteSelected = async () => {
    try {
      setLoading(true);
      await bulkDeleteAccounts(selectedRowKeys);
      message.success(`${selectedRowKeys.length} accounts deleted successfully`);
      setSelectedRowKeys([]);
      loadData();
    } catch (error) {
      console.error('Failed to delete selected accounts:', error);
      message.error('Failed to delete selected accounts');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAccount(id);
      message.success('Account deleted successfully.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to delete account.');
    }
  };

  // CSV File upload handling
  const handleCSVUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const parsed = parseCSV(content, currentRole);
      setParsedUsers(parsed);
      setBulkResult(null);
    };
    reader.readAsText(file);
    return false; // Prevent default upload POST
  };

  const downloadCSVTemplate = () => {
    const headers = ['login_name', 'login_username', 'login_password', 'contact', 'email', 'login_role', 'institution_id', 'login_end_date', 'courses', 'courses_access_type'];
    const sampleRow1 = ['John Doe', 'johndoe', 'Pass@123', '9876543210', 'john@example.com', 'USER', '1', '2026-12-31', 'Physics 101|Chemistry 202', 'LIVE/VOD'];
    const sampleRow2 = ['Jane Smith', 'janesmith', 'Pass@456', '9123456780', 'jane@example.com', 'STUDIO', '', '2027-05-15', 'Maths 301', 'LIVE'];

    const content = [
      headers.join(','),
      sampleRow1.map(v => `"${v.replace(/"/g, '""')}"`).join(','),
      sampleRow2.map(v => `"${v.replace(/"/g, '""')}"`).join(',')
    ].join('\n');

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'Bulk_Users_Upload_Template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkSubmit = async () => {
    if (parsedUsers.length === 0) {
      message.warning('Please upload a CSV file with valid user records.');
      return;
    }
    const validRows = parsedUsers.filter(u => u.isValid);
    if (validRows.length === 0) {
      message.error('No valid user rows found to import.');
      return;
    }

    setBulkLoading(true);
    try {
      const payload = {
        users: validRows,
        global_course_ids: globalCourseIds,
        global_courses_access_type: globalAccessType
      };

      const res = await bulkCreateAccounts(payload);
      setBulkResult(res);
      message.success(res.message || 'Bulk creation complete.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to complete bulk creation.');
    } finally {
      setBulkLoading(false);
    }
  };

  // Perform filtering reactively on the client side
  const filteredData = data.filter(item => {
    if (isStudio && (item.login_role === 'ADMIN' || item.login_role === 'STUDIO')) {
      return false;
    }
    const nameMatch = item.login_name ? item.login_name.toLowerCase().includes(searchText.toLowerCase()) : false;
    const usernameMatch = item.login_username ? item.login_username.toLowerCase().includes(searchText.toLowerCase()) : false;
    const contactMatch = item.contact ? item.contact.toLowerCase().includes(searchText.toLowerCase()) : false;
    const emailMatch = item.email ? item.email.toLowerCase().includes(searchText.toLowerCase()) : false;
    const institutionMatch = item.institution_name ? item.institution_name.toLowerCase().includes(searchText.toLowerCase()) : false;
    const lastLoginMatch = item.updated_at ? (formatDate(item.updated_at) || '').includes(searchText) : false;
    
    const matchesSearch = !searchText || nameMatch || usernameMatch || contactMatch || emailMatch || institutionMatch || lastLoginMatch;
    const matchesRole = !selectedRole || item.login_role === selectedRole;
    const matchesStatus = !selectedStatus || (item.login_status || 'Active') === selectedStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const columns = [
    { 
      title: 'User Details', 
      key: 'user_details', 
      sorter: (a, b) => (a.login_name || a.login_username || '').localeCompare(b.login_name || b.login_username || ''),
      render: (_, record) => {
        const lastLoginDate = record.updated_at ? formatDate(record.updated_at) : null;
        const lastLoginDateTime = record.updated_at ? formatDateTime(record.updated_at) : null;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 200 }}>
            {/* Full Name */}
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-color, inherit)' }}>
              {record.login_name || 'Unnamed User'}
            </span>

            {/* Username */}
            <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600 }}>
              @{record.login_username}
            </span>

            {/* Contact & Email */}
            {(record.contact || record.email) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1, marginBottom: 1 }}>
                {record.contact && (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary, rgba(255, 255, 255, 0.75))', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <PhoneOutlined style={{ fontSize: 11, color: '#10b981' }} />
                    <span>{record.contact}</span>
                  </span>
                )}
                {record.email && (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary, rgba(255, 255, 255, 0.75))', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <MailOutlined style={{ fontSize: 11, color: '#38bdf8' }} />
                    <span style={{ wordBreak: 'break-all' }}>{record.email}</span>
                  </span>
                )}
              </div>
            )}

            {/* Linked Institution */}
            {record.institution_name ? (
              <span style={{ 
                fontSize: 11, 
                color: 'var(--text-secondary, rgba(255, 255, 255, 0.65))', 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 4 
              }}>
                <BankOutlined style={{ fontSize: 11, color: '#fbbf24' }} />
                <span>{record.institution_name}</span>
              </span>
            ) : (
              <span style={{ 
                fontSize: 11, 
                color: 'var(--text-secondary, rgba(255, 255, 255, 0.3))', 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 4 
              }}>
                <BankOutlined style={{ fontSize: 11 }} />
                <span>No Institution</span>
              </span>
            )}

            {/* Last Login Time */}
            {lastLoginDate ? (
              <Tooltip title={`Last Login: ${lastLoginDateTime}`}>
                <span style={{ 
                  fontSize: 11, 
                  color: 'var(--text-secondary, rgba(255, 255, 255, 0.45))', 
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer'
                }}>
                  <ClockCircleOutlined style={{ fontSize: 10, color: '#10b981' }} />
                  <span>Last Login: {lastLoginDateTime || lastLoginDate}</span>
                </span>
              </Tooltip>
            ) : (
              <span style={{ 
                fontSize: 11, 
                color: 'var(--text-secondary, rgba(255, 255, 255, 0.3))', 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4 
              }}>
                <ClockCircleOutlined style={{ fontSize: 10 }} />
                <span>Last Login: Never</span>
              </span>
            )}
          </div>
        );
      }
    },
    { 
      title: 'Course Completion', 
      dataIndex: 'course_completion_percentage', 
      key: 'course_completion_percentage',
      width: 170,
      sorter: (a, b) => (a.course_completion_percentage || 0) - (b.course_completion_percentage || 0),
      render: (pct) => {
        const val = Number(pct || 0);
        let color = '#38bdf8';
        let badgeColor = 'blue';
        if (val >= 80) { color = '#10b981'; badgeColor = 'green'; }
        else if (val >= 40) { color = '#f59e0b'; badgeColor = 'gold'; }
        else if (val > 0) { color = '#38bdf8'; badgeColor = 'cyan'; }
        else { color = 'rgba(255,255,255,0.2)'; badgeColor = 'default'; }

        return (
          <div style={{ minWidth: 120 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <Tag color={badgeColor} style={{ fontWeight: 700, margin: 0, fontSize: 11, padding: '0 6px', borderRadius: 4 }}>
                <TrophyOutlined style={{ marginRight: 4 }} />
                {val}%
              </Tag>
            </div>
            <Progress 
              percent={val} 
              size="small" 
              strokeColor={color} 
              showInfo={false} 
              style={{ margin: 0 }}
            />
          </div>
        );
      }
    },
    {
      title: 'Assigned Courses',
      key: 'courses_access',
      render: (_, record) => {
        const accessList = record.courses_access || [];
        if (accessList.length === 0) {
          return <span style={{ color: 'rgba(255,255,255,0.3)' }}>No Courses Assigned</span>;
        }
        return (
          <Space direction="vertical" size={2} style={{ maxWidth: 220 }}>
            {accessList.map(ca => (
              <Tag key={ca.courses_access_id} color="geekblue" style={{ margin: 2, fontSize: 11 }}>
                <BookOutlined style={{ marginRight: 4 }} />
                {ca.courses_title} <span style={{ fontWeight: 600, color: '#fbbf24' }}>({ca.courses_access_type})</span>
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Role',
      dataIndex: 'login_role',
      key: 'login_role',
      sorter: (a, b) => (a.login_role || '').localeCompare(b.login_role || ''),
      render: (role) => (
        <Tag color={role === 'ADMIN' ? 'gold' : role === 'STUDIO' ? 'purple' : 'blue'}>
          {role || 'USER'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'login_status',
      key: 'login_status',
      sorter: (a, b) => (a.login_status || '').localeCompare(b.login_status || ''),
      render: (status) => (
        <Tag color={status === 'Active' ? 'green' : 'red'}>
          {(status || 'Active').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Expiry Date',
      dataIndex: 'login_end_date',
      key: 'login_end_date',
      sorter: (a, b) => {
        const dateA = a.login_end_date ? new Date(a.login_end_date).getTime() : 0;
        const dateB = b.login_end_date ? new Date(b.login_end_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (dateStr) => {
        if (!dateStr) return <span style={{ color: 'rgba(255,255,255,0.3)' }}>Never</span>;
        const expiryDate = new Date(dateStr);
        const today = new Date();
        today.setHours(0,0,0,0);
        expiryDate.setHours(0,0,0,0);
        
        const expired = today > expiryDate;
        const formatted = formatDate(dateStr);
        
        return expired ? (
          <Tag color="red" style={{ fontWeight: 500 }}>{formatted} (Expired)</Tag>
        ) : (
          <span style={{ color: '#10b981' }}>{formatted}</span>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="View User Analytics">
            <Button 
              size="small" 
              icon={<BarChartOutlined />} 
              style={{ color: '#06b6d4', borderColor: '#06b6d4' }} 
              onClick={() => handleOpenAnalytics(record)} 
            />
          </Tooltip>
          <Tooltip title="Edit Account">
            <Button size="small" icon={<EditOutlined />} onClick={() => { 
              setEditingItem(record); 
              const existingCourseIds = (record.courses_access || []).map(c => c.courses_id);
              const firstAccessType = (record.courses_access || [])[0]?.courses_access_type || 'LIVE/VOD';

              form.setFieldsValue({
                ...record,
                login_end_date: record.login_end_date ? record.login_end_date.split('T')[0] : '',
                institution_id: record.institution_id || undefined,
                course_ids: existingCourseIds,
                courses_access_type: firstAccessType
              }); 
              setIsModalOpen(true); 
            }} />
          </Tooltip>
          <Tooltip title="Reset Password">
            <Button size="small" icon={<KeyOutlined />} style={{ color: '#eab308', borderColor: '#eab308' }} onClick={() => { 
              setResettingItem(record);
              setIsResetModalOpen(true); 
            }} />
          </Tooltip>
          <Popconfirm title="Delete this account?" onConfirm={() => handleDelete(record.login_id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const parsedPreviewColumns = [
    { title: 'Row #', dataIndex: 'rowNumber', key: 'rowNumber', width: 65 },
    { title: 'Full Name', dataIndex: 'login_name', key: 'login_name', render: text => text || '-' },
    { title: 'Contact', dataIndex: 'contact', key: 'contact', render: text => text || '-' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: text => text || '-' },
    { title: 'Username', dataIndex: 'login_username', key: 'login_username', render: text => <strong>{text}</strong> },
    { title: 'Password', dataIndex: 'login_password', key: 'login_password', render: () => '••••••••' },
    { title: 'Role', dataIndex: 'login_role', key: 'login_role', render: role => <Tag color="blue">{role || 'USER'}</Tag> },
    { title: 'Institution', key: 'institution', render: (_, record) => record.institution_id || record.institution_name || '-' },
    { title: 'Courses', dataIndex: 'courses', key: 'courses', render: text => text || (globalCourseIds.length > 0 ? `${globalCourseIds.length} course(s) selected globally` : 'None') },
    { title: 'Access Type', dataIndex: 'courses_access_type', key: 'courses_access_type', render: text => <Tag color="orange">{text || globalAccessType}</Tag> },
    {
      title: 'Status',
      key: 'isValid',
      render: (_, record) => record.isValid ? (
        <Tag color="success" icon={<CheckCircleOutlined />}>Valid</Tag>
      ) : (
        <Tooltip title={record.validationErrors.join(', ')}>
          <Tag color="error" icon={<ExclamationCircleOutlined />}>Invalid</Tag>
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12, width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Accounts Management</h2>
        </div>
        <Space>
          <Button type="default" icon={<UploadOutlined />} style={{ borderColor: '#6366f1', color: '#6366f1' }} onClick={() => { setParsedUsers([]); setBulkResult(null); setIsBulkModalOpen(true); }}>
            Bulk CSV Upload
          </Button>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title="Delete selected accounts?"
              description={`Are you sure to delete ${selectedRowKeys.length} accounts? This will also remove their courses and modules access.`}
              onConfirm={handleDeleteSelected}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setIsModalOpen(true); }}>
            Add Account
          </Button>
        </Space>
      </div>

      {/* Search and Filters Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input 
          placeholder="Search by name, username, or institution..." 
          onChange={(e) => setSearchText(e.target.value)} 
          value={searchText} 
          style={{ width: 340, borderRadius: 6 }} 
          allowClear 
        />
        <Select showSearch optionFilterProp="children" 
          placeholder="Filter by Role" 
          onChange={(value) => setSelectedRole(value)} 
          value={selectedRole} 
          style={{ width: 160 }} 
          allowClear
        >
          {!isStudio && <Select.Option value="ADMIN">ADMIN</Select.Option>}
          {!isStudio && <Select.Option value="STUDIO">STUDIO</Select.Option>}
          <Select.Option value="USER">USER</Select.Option>
        </Select>
        <Select showSearch optionFilterProp="children" 
          placeholder="Filter by Status" 
          onChange={(value) => setSelectedStatus(value)} 
          value={selectedStatus} 
          style={{ width: 160 }} 
          allowClear
        >
          <Select.Option value="Active">Active</Select.Option>
          <Select.Option value="Inactive">Inactive</Select.Option>
        </Select>
        {(searchText || selectedRole || selectedStatus) && (
          <Button type="link" onClick={() => { setSearchText(''); setSelectedRole(undefined); setSelectedStatus(undefined); }} style={{ padding: 0 }}>
            Clear Filters
          </Button>
        )}
      </div>

      <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12, width: '100%' }} bodyStyle={{ padding: 20 }}>
        <Table 
          dataSource={filteredData} 
          columns={columns} 
          rowSelection={rowSelection}
          rowKey="login_id" 
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{ 
            pageSize: 30,
            defaultPageSize: 30, 
            showSizeChanger: true, 
            pageSizeOptions: ['10', '20', '30', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} accounts`
          }} 
        />
      </Card>

      {/* Add / Edit Account Modal */}
      <Modal title={editingItem ? 'Edit Account' : 'Add Account'} open={isModalOpen} onCancel={() => setIsModalOpen(false)} footer={null} destroyOnClose width={600}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="login_name" label="Full Name" rules={[{ required: true, message: 'Please input full name!' }]}>
            <Input placeholder="e.g. John Doe" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact" label="Contact (Phone / Mobile)">
                <Input placeholder="e.g. 9876543210" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="email" 
                label="Email Address"
                rules={[{ type: 'email', message: 'Please enter a valid email address!' }]}
              >
                <Input placeholder="e.g. user@example.com" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item 
            name="login_username" 
            label="Username" 
            rules={[
              { required: true, message: 'Please input username!' },
              {
                validator: async (_, value) => {
                  if (!value || editingItem) {
                    return Promise.resolve();
                  }
                  const trimmed = value.trim();
                  if (!trimmed) {
                    return Promise.resolve();
                  }
                  // Check locally loaded accounts first
                  const localExists = data.some(
                    acc => (acc.login_username || '').trim().toLowerCase() === trimmed.toLowerCase()
                  );
                  if (localExists) {
                    return Promise.reject(new Error('Username already exists. Please choose a different username.'));
                  }
                  // Real-time API check against full database
                  try {
                    const res = await checkAccountUsername(trimmed);
                    if (res && res.exists) {
                      return Promise.reject(new Error('Username already exists. Please choose a different username.'));
                    }
                  } catch (err) {
                    // Fallback to backend validation upon submission if check fails
                  }
                  return Promise.resolve();
                }
              }
            ]}
            hasFeedback
          >
            <Input placeholder="e.g. johndoe" disabled={!!editingItem} />
          </Form.Item>
          {!editingItem && (
            <>
              <Form.Item name="login_password" label="Password" rules={[{ required: true, message: 'Please input password!' }]}>
                <Input.Password placeholder="Enter password" />
              </Form.Item>
              <Form.Item
                name="confirm_password"
                label="Confirm Password"
                dependencies={['login_password']}
                rules={[
                  { required: true, message: 'Please confirm your password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('login_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" />
              </Form.Item>
            </>
          )}
          <Form.Item name="login_role" label="Access Role" rules={[{ required: true }]} initialValue="USER">
            <Select showSearch optionFilterProp="children">
              {!isStudio && <Select.Option value="ADMIN">ADMIN</Select.Option>}
              {!isStudio && <Select.Option value="STUDIO">STUDIO</Select.Option>}
              <Select.Option value="USER">USER</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="institution_id" label="Linked Institution">
            <Select showSearch optionFilterProp="children" placeholder="Select learning institution (Optional)" allowClear>
              {institutions.map(inst => (
                <Select.Option key={inst.institution_id} value={inst.institution_id}>
                  {inst.institution_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider style={{ margin: '16px 0', borderColor: 'var(--border-color)' }}>Course Access Configuration</Divider>

          <Form.Item name="course_ids" label="Assign Courses">
            <Select showSearch mode="multiple" placeholder="Select courses to grant access" allowClear optionFilterProp="children">
              {courses.map(c => (
                <Select.Option key={c.courses_id} value={c.courses_id}>
                  {c.courses_name || c.courses_title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="courses_access_type" label="Access Type" initialValue="LIVE/VOD">
            <Select showSearch optionFilterProp="children">
              <Select.Option value="LIVE/VOD">LIVE / VOD (Full Access)</Select.Option>
              <Select.Option value="LIVE">LIVE Streaming Only</Select.Option>
              <Select.Option value="VOD">VOD (Recorded Videos Only)</Select.Option>
              <Select.Option value="EDIT">EDIT / Authoring Permission</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="login_status" label="Status" rules={[{ required: true }]} initialValue="Active">
            <Select showSearch optionFilterProp="children">
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="login_end_date" label="Expiry Date (Optional)">
            <Input type="date" style={{ color: '#000' }} />
          </Form.Item>
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk CSV Upload Modal */}
      <Modal 
        title={
          <Space>
            <FileTextOutlined style={{ color: '#6366f1' }} />
            <span>Bulk Add Users via CSV Upload</span>
          </Space>
        } 
        open={isBulkModalOpen} 
        onCancel={() => setIsBulkModalOpen(false)} 
        footer={null} 
        destroyOnClose 
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert 
            type="info" 
            showIcon 
            message="CSV Upload Instructions" 
            description={
              <div>
                Upload a <strong>.csv</strong> file containing user details. You can specify course access directly per row in the CSV or select global courses and access type below.
                <div style={{ marginTop: 8 }}>
                  <Button icon={<DownloadOutlined />} size="small" type="primary" ghost onClick={downloadCSVTemplate}>
                    Download Sample CSV Template
                  </Button>
                </div>
              </div>
            }
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, background: 'rgba(255,255,255,0.03)', padding: 12, borderRadius: 8, border: '1px dashed var(--border-color)' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: 'var(--text-color)' }}>
              Target Courses (Applied if not specified per row)
            </label>
            <Select showSearch optionFilterProp="children" 
              mode="multiple" 
              placeholder="Select courses to assign" 
              value={globalCourseIds} 
              onChange={setGlobalCourseIds} 
              style={{ width: '100%' }}
              allowClear
            >
              {courses.map(c => (
                <Select.Option key={c.courses_id} value={c.courses_id}>{c.courses_name || c.courses_title}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: 'var(--text-color)' }}>
              Target Access Type
            </label>
            <Select showSearch optionFilterProp="children" 
              value={globalAccessType} 
              onChange={setGlobalAccessType} 
              style={{ width: '100%' }}
            >
              <Select.Option value="LIVE/VOD">LIVE / VOD (Full Access)</Select.Option>
              <Select.Option value="LIVE">LIVE Streaming Only</Select.Option>
              <Select.Option value="VOD">VOD (Recorded Videos Only)</Select.Option>
              <Select.Option value="EDIT">EDIT / Authoring Permission</Select.Option>
            </Select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Upload.Dragger 
            accept=".csv" 
            beforeUpload={handleCSVUpload} 
            showUploadList={false}
            style={{ padding: 16, borderRadius: 8, background: 'var(--panel-color)', border: '1px dashed #6366f1' }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 32, color: '#6366f1' }} />
            </p>
            <p className="ant-upload-text" style={{ color: 'var(--text-color)', fontWeight: 500 }}>
              Click or drag CSV file to this area to parse users
            </p>
            <p className="ant-upload-hint" style={{ color: 'var(--text-secondary)' }}>
              Expected CSV headers: login_name, contact, email, login_username, login_password, login_role, institution_id, login_end_date, courses, courses_access_type
            </p>
          </Upload.Dragger>
        </div>

        {parsedUsers.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ fontWeight: 600, color: 'var(--text-color)' }}>
                Parsed CSV Preview ({parsedUsers.filter(u => u.isValid).length} Valid / {parsedUsers.length} Total Rows)
              </Text>
            </div>
            <Table 
              dataSource={parsedUsers} 
              columns={parsedPreviewColumns} 
              rowKey="rowNumber" 
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'] }} 
              size="small"
              bordered
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}

        {bulkResult && (
          <div style={{ marginBottom: 16 }}>
            <Alert 
              type={bulkResult.failedCount > 0 ? "warning" : "success"}
              showIcon
              message={`Import Summary: ${bulkResult.createdCount} Accounts Created, ${bulkResult.failedCount} Failed`}
              description={
                bulkResult.errors && bulkResult.errors.length > 0 ? (
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
                    {bulkResult.errors.map((e, idx) => (
                      <li key={idx}>Row {e.row} ({e.username}): {e.error}</li>
                    ))}
                  </ul>
                ) : null
              }
            />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
          <Button onClick={() => setIsBulkModalOpen(false)}>Close</Button>
          <Button 
            type="primary" 
            icon={<UploadOutlined />} 
            loading={bulkLoading} 
            disabled={parsedUsers.length === 0} 
            onClick={handleBulkSubmit}
          >
            Import Users ({parsedUsers.filter(u => u.isValid).length})
          </Button>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal title={`Reset Password: ${resettingItem?.login_username || ''}`} open={isResetModalOpen} onCancel={() => setIsResetModalOpen(false)} footer={null} destroyOnClose>
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item name="login_password" label="New Password" rules={[{ required: true, message: 'Please input new password!' }]}>
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={['login_password']}
            rules={[
              { required: true, message: 'Please confirm the new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('login_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsResetModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Reset Password</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* User Analytics & Scores Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
            <Space size="middle">
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-color)' }}>
                📊 Analytics for {analyticsUser?.login_name || analyticsUser?.login_username}
              </span>
              <Tag color={analyticsUser?.login_role === 'ADMIN' ? 'gold' : analyticsUser?.login_role === 'STUDIO' ? 'purple' : 'blue'}>
                {analyticsUser?.login_role || 'USER'}
              </Tag>
              {analyticsUser?.institution_name && (
                <Tag color="cyan">🏛️ {analyticsUser.institution_name}</Tag>
              )}
              {analyticsUser?.contact && (
                <Tag color="green">📞 {analyticsUser.contact}</Tag>
              )}
              {analyticsUser?.email && (
                <Tag color="geekblue">✉️ {analyticsUser.email}</Tag>
              )}
            </Space>
            <Button 
              size="small" 
              icon={<SyncOutlined spin={analyticsLoading} />} 
              onClick={handleRefreshAnalytics}
            >
              Refresh
            </Button>
          </div>
        }
        open={isAnalyticsModalOpen}
        onCancel={() => {
          setIsAnalyticsModalOpen(false);
          setAnalyticsUser(null);
        }}
        width={1180}
        style={{ top: 24, maxWidth: '96vw' }}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsAnalyticsModalOpen(false)}>
            Close
          </Button>
        ]}
        destroyOnClose
        styles={{ body: { maxHeight: '78vh', overflowY: 'auto', padding: '16px 8px' } }}
      >
        <Spin spinning={analyticsLoading}>
          <Tabs
            defaultActiveKey="watch"
            items={[
              {
                key: 'watch',
                label: (
                  <span>
                    <VideoCameraOutlined style={{ marginRight: 6 }} />
                    Watch Analytics ({watchAnalytics?.records?.length || 0})
                  </span>
                ),
                children: (
                  <div>
                    {/* Summary Cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Watch Time</span>}
                            value={watchAnalytics?.summary?.total_watched_formatted || '00:00:00'}
                            valueStyle={{ color: '#38bdf8', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<ClockCircleOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Sessions</span>}
                            value={watchAnalytics?.summary?.total_sessions || 0}
                            valueStyle={{ color: '#a855f7', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<PlaySquareOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Unique Videos</span>}
                            value={watchAnalytics?.summary?.unique_videos_watched || 0}
                            valueStyle={{ color: '#10b981', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<VideoCameraOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Course Completion</span>}
                            value={`${watchAnalytics?.summary?.percentage_watched || 0}%`}
                            valueStyle={{ color: '#f59e0b', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<TrophyOutlined />}
                          />
                          <Progress 
                            percent={Number(watchAnalytics?.summary?.percentage_watched || 0)} 
                            size="small" 
                            strokeColor="#f59e0b" 
                            showInfo={false} 
                            style={{ marginTop: 4 }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Detailed Watch History Table */}
                    <Table
                      dataSource={watchAnalytics?.records || []}
                      rowKey={(record, index) => record.video_watch_track_id || index}
                      size="small"
                      scroll={{ x: 'max-content' }}
                      pagination={{ 
                        pageSize: 30, 
                        defaultPageSize: 30, 
                        showSizeChanger: true, 
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} watch logs` 
                      }}
                      columns={[
                        {
                          title: 'Video Title',
                          dataIndex: 'video_title',
                          key: 'video_title',
                          render: (title, r) => (
                            <div>
                              <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>{title || 'Untitled Video'}</div>
                              <Tag color={r.video_type === 'LIVE' ? 'red' : 'blue'} style={{ fontSize: 10, marginTop: 2 }}>
                                {r.video_type || 'VOD'}
                              </Tag>
                            </div>
                          )
                        },
                        {
                          title: 'Course & Module',
                          key: 'course_module',
                          render: (_, r) => (
                            <div style={{ fontSize: 12 }}>
                              <div style={{ color: '#38bdf8' }}>{r.courses_name || 'N/A'}</div>
                              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{r.modules_name || ''}</div>
                            </div>
                          )
                        },
                        {
                          title: 'Duration Watched',
                          dataIndex: 'video_duration',
                          key: 'video_duration',
                          width: 135,
                          render: (dur) => (
                            <Tag color="cyan" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              ⏱ {dur || '00:00:00'}
                            </Tag>
                          )
                        },
                        {
                          title: 'Start Time',
                          dataIndex: 'start_time',
                          key: 'start_time',
                          width: 165,
                          render: (time) => time ? new Date(time).toLocaleString() : 'N/A'
                        },
                        {
                          title: 'IP Address',
                          dataIndex: 'ip_addess',
                          key: 'ip_addess',
                          width: 135,
                          render: (ip) => ip ? <span style={{ fontFamily: 'monospace', fontSize: 11 }}><GlobalOutlined style={{ marginRight: 4 }} />{ip}</span> : 'N/A'
                        }
                      ]}
                      locale={{
                        emptyText: <Empty description="No video watch records found for this user." />
                      }}
                    />
                  </div>
                )
              },
              {
                key: 'assessments',
                label: (
                  <span>
                    <TrophyOutlined style={{ marginRight: 6 }} />
                    Assessments & Quizzes ({assessmentAnalytics?.attempts?.length || 0})
                  </span>
                ),
                children: (
                  <div>
                    {/* Summary Cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(56, 189, 248, 0.08)', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Attempts</span>}
                            value={assessmentAnalytics?.summary?.total_attempts || 0}
                            valueStyle={{ color: '#38bdf8', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<BookOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(168, 85, 247, 0.08)', border: '1px solid rgba(168, 85, 247, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Total Points</span>}
                            value={assessmentAnalytics?.summary?.total_score || 0}
                            valueStyle={{ color: '#a855f7', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<BarChartOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Average Score</span>}
                            value={`${assessmentAnalytics?.summary?.average_percentage || 0}%`}
                            valueStyle={{ color: '#10b981', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<TrophyOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small" style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                          <Statistic
                            title={<span style={{ color: 'var(--text-color)', fontSize: 12 }}>Best Score</span>}
                            value={`${assessmentAnalytics?.summary?.best_percentage || 0}%`}
                            valueStyle={{ color: '#f59e0b', fontWeight: 'bold', fontSize: 20 }}
                            prefix={<CheckCircleOutlined />}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Detailed Quiz Attempts Table */}
                    <Table
                      dataSource={assessmentAnalytics?.attempts || []}
                      rowKey={(record, index) => record.attempt_id || index}
                      size="small"
                      scroll={{ x: 'max-content' }}
                      pagination={{ 
                        pageSize: 30, 
                        defaultPageSize: 30, 
                        showSizeChanger: true, 
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} quiz attempts` 
                      }}
                      columns={[
                        {
                          title: 'Assessment / Quiz Title',
                          dataIndex: 'assessment_title',
                          key: 'assessment_title',
                          render: (title) => <strong>{title || 'Untitled Assessment'}</strong>
                        },
                        {
                          title: 'Course & Module',
                          key: 'course_module',
                          render: (_, r) => (
                            <div style={{ fontSize: 12 }}>
                              <div style={{ color: '#38bdf8' }}>{r.courses_name || 'N/A'}</div>
                              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>{r.modules_name || ''}</div>
                            </div>
                          )
                        },
                        {
                          title: 'Score',
                          dataIndex: 'score',
                          key: 'score',
                          width: 90,
                          render: (score) => (
                            <Tag color="purple" style={{ fontWeight: 600 }}>{score} pts</Tag>
                          )
                        },
                        {
                          title: 'Percentage',
                          dataIndex: 'percentage',
                          key: 'percentage',
                          width: 110,
                          sorter: (a, b) => (a.percentage || 0) - (b.percentage || 0),
                          render: (pct) => {
                            const num = Number(pct || 0);
                            const color = num >= 75 ? 'green' : num >= 50 ? 'gold' : 'red';
                            return <Tag color={color} style={{ fontWeight: 'bold' }}>{num}%</Tag>;
                          }
                        },
                        {
                          title: 'Correct / Total',
                          key: 'correct',
                          width: 120,
                          render: (_, r) => `${r.correct_count || 0} / ${r.total_questions || 0}`
                        },
                        {
                          title: 'Attempted Date',
                          dataIndex: 'attempted_at',
                          key: 'attempted_at',
                          width: 165,
                          render: (date) => date ? new Date(date).toLocaleString() : 'N/A'
                        },
                        {
                          title: 'Details',
                          key: 'details',
                          width: 90,
                          render: (_, r) => (
                            <Button 
                              size="small" 
                              icon={<EyeOutlined />} 
                              onClick={() => {
                                setSelectedAttemptBreakdown(r);
                                setIsBreakdownModalOpen(true);
                              }}
                            >
                              Review
                            </Button>
                          )
                        }
                      ]}
                      locale={{
                        emptyText: <Empty description="No assessment or quiz attempts found for this user." />
                      }}
                    />
                  </div>
                )
              }
            ]}
          />
        </Spin>
      </Modal>

      {/* Question-by-Question Breakdown Modal */}
      <Modal
        title={`Quiz Review: ${selectedAttemptBreakdown?.assessment_title || ''}`}
        open={isBreakdownModalOpen}
        onCancel={() => {
          setIsBreakdownModalOpen(false);
          setSelectedAttemptBreakdown(null);
        }}
        footer={[
          <Button key="close" onClick={() => setIsBreakdownModalOpen(false)}>
            Close Review
          </Button>
        ]}
        width={880}
        style={{ top: 28, maxWidth: '96vw' }}
        destroyOnClose
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', padding: '16px 8px' } }}
      >
        {selectedAttemptBreakdown?.breakdown && selectedAttemptBreakdown.breakdown.length > 0 ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(255,255,255,0.04)', padding: 12, borderRadius: 8 }}>
              <div><strong>Score:</strong> {selectedAttemptBreakdown.score} pts ({selectedAttemptBreakdown.percentage}%)</div>
              <div><strong>Correct:</strong> {selectedAttemptBreakdown.correct_count} / {selectedAttemptBreakdown.total_questions}</div>
              <div><strong>Date:</strong> {selectedAttemptBreakdown.attempted_at ? new Date(selectedAttemptBreakdown.attempted_at).toLocaleString() : 'N/A'}</div>
            </div>

            {selectedAttemptBreakdown.breakdown.map((q, idx) => (
              <Card 
                key={idx} 
                size="small" 
                style={{ 
                  borderLeft: `4px solid ${q.is_correct ? '#10b981' : q.has_answered ? '#ef4444' : '#64748b'}`,
                  background: 'rgba(255,255,255,0.02)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>Q{idx + 1}. {q.question_text}</strong>
                  <Tag color={q.is_correct ? 'green' : q.has_answered ? 'red' : 'default'}>
                    {q.is_correct ? `+${q.points || 1} pts` : q.has_answered ? `${q.marks_earned || 0} pts` : 'Skipped'}
                  </Tag>
                </div>
                {q.question_image && (
                  <img src={q.question_image} alt="Question" style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', marginBottom: 8, borderRadius: 4 }} />
                )}
                <div style={{ fontSize: 13, lineHeight: '20px' }}>
                  <div>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Student Answer: </span>
                    <span style={{ color: q.is_correct ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {q.user_answer || <i style={{ color: 'rgba(255,255,255,0.3)' }}>No Answer</i>}
                    </span>
                  </div>
                  {!q.is_correct && (
                    <div>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Correct Answer: </span>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>{q.correct_answer}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        ) : (
          <Empty description="No question breakdown details available for this attempt." />
        )}
      </Modal>
    </div>
  );
};

export default AccountsManagementPage;
